import { useCallback } from 'react';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import isEqualWith from 'lodash/isEqualWith';
import { axiosCustomInstance, axiosInstance } from './axios';
import { ToastWithError } from '../../../navigation/routes/toastProvider';
import { stringsRes } from '../../../resources/strings/index';
import { types } from '../../modules/pendings';
import { DISCARDED_CODE, DISCARDED_TIME, RETRY_MUTATION } from '../../../utils/constants';
import { Options } from './useMyQuery';
import { ApiType } from '../../apiTypes';
import featureFlag from '../../../config/featureFlag';

export interface MutationInfos {
    method?: 'post' | 'delete' | 'put' | 'patch';
    path: string;
    type: string; // UNIQUE
    params?: any;
    config?: any;
    apiType?: ApiType;
}

const ERROR_MESSAGE = {
    NO_DRIVER_AVAILABLE: stringsRes.booking.status.no_driver_available,
    UPDATE_NOT_ALLOWED: stringsRes.booking.status.no_update,
    DUPLICATE_RIDE: stringsRes.booking.status.duplicate_ride,
    DEFAULT: stringsRes.errors.defaultError,
};

const axiosInstances = { [ApiType.PRIVAPI]: axiosInstance, [ApiType.ID]: axiosCustomInstance };

const getFormData = (object) =>
    Object.keys(object).reduce((formData, key) => {
        if (key === 'file') {
            formData.append('file[0]', object[key]);
        } else {
            formData.append(key, typeof object[key] === 'object' ? JSON.stringify(object[key]) : object[key]);
        }
        return formData;
    }, new FormData());

const axiosFetcher = async (mutationInfos: MutationInfos): Promise<any> => {
    const { method = 'post', path, params, config, apiType = ApiType.PRIVAPI } = mutationInfos;
    console.log('MUTATION - METHOD:', method, 'URL:', path, 'PARAMS: ', params);
    let response: any;
    const instance = axiosInstances[apiType];

    // Convert params to multipart if asked
    let multiPartParams = null;
    if (config?.headers['Content-Type'] === 'multipart/form-data') {
        multiPartParams = getFormData(params);
    }

    switch (method) {
        case 'post':
        case 'put':
        case 'patch':
            response = await instance[method](path, multiPartParams ?? params, config);
            if (response.data.codeError) {
                throw new Error(ERROR_MESSAGE?.[response.data.codeError] || ERROR_MESSAGE.DEFAULT);
            }
            break;
        case 'delete':
            response = await instance[method](path, { data: params });
            break;
        default:
            return null;
    }

    console.log('MUTATION - METHOD', method, 'URL:', path, 'RESPONSE:', response.data);

    return response.data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useMyMutation<K>(
    mutation: (variables: K) => MutationInfos,
    options: Options<K>,
): UseMutationResult<unknown, Error, K, unknown> {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { onError, onSuccess, ...otherOptions } = options;

    const mutationFn = useCallback(
        async (variables: K) => {
            const mutationInfos = mutation(variables || ({} as K));
            const { type } = mutationInfos;
            if (featureFlag.pending.active) {
                dispatch({ type: types.START_PENDING_OPERATION, payload: { type } });
            }

            if (featureFlag.queryDuplicateRemover.active) {
                const lastData: any = queryClient.getQueryData('lastSubmittedData');
                const { date, ...lastDataNoDate } = lastData || {};
                // console.log(lastDataNoDate, mutationInfos, isEqualWith(lastDataNoDate, mutationInfos));
                if (
                    isEqualWith(lastDataNoDate, mutationInfos) &&
                    Math.abs(date - new Date().getTime()) < DISCARDED_TIME
                ) {
                    // Prevent duplicate submission
                    console.warn('Discarded request', mutationInfos);
                    return Promise.reject(new Error('Duplicated', { cause: DISCARDED_CODE }));
                }
                queryClient.setQueryData('lastSubmittedData', { ...mutationInfos, date: new Date().getTime() });
            }

            return axiosFetcher(mutationInfos);
        },
        [queryClient, dispatch, mutation],
    );

    return useMutation<any, Error, K, unknown>(mutationFn, {
        onSuccess(data, variables, context) {
            onSuccess?.(data, variables, context);
        },
        onError: (error: Error, variables: any, context: unknown) => {
            if (onError) {
                onError(error, variables, context);
            } else {
                ToastWithError(error);
            }
        },
        onSettled() {
            if (featureFlag.pending.active) {
                const { type } = mutation({} as K);
                dispatch({ type: types.STOP_PENDING_OPERATION, payload: { type } });
            }
        },
        retry(failureCount) {
            if (featureFlag.pending.active) {
                const { type } = mutation({} as K);
                dispatch({ type: types.STOP_PENDING_OPERATION, payload: { type } });
            }
            return failureCount < RETRY_MUTATION;
        },
        ...otherOptions,
        // useErrorBoundary: !configs?.appConfig?.isDev, // we want toast msg on dev & silent err on othen env
    });
}
